// Browser / phone push for this device. The worker at /push-sw.js (also the offline shell, D-140) shows notifications and opens the
// CRM when one is tapped; the server decides who is notified. "On" means this browser has both the
// permission and a subscription the server knows about.
import { api, get, post } from '../../lib/api.js';

export type PushState = 'unsupported' | 'blocked' | 'off' | 'on';

const WORKER = '/push-sw.js';

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return (await navigator.serviceWorker.getRegistration(WORKER)) ?? navigator.serviceWorker.register(WORKER);
}

export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  if (Notification.permission !== 'granted') return 'off';
  try {
    const reg = await navigator.serviceWorker.getRegistration(WORKER);
    return (await reg?.pushManager.getSubscription()) ? 'on' : 'off';
  } catch {
    return 'off';
  }
}

function keyBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = base64Url
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(base64Url.length / 4) * 4, '=');
  const raw = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Asks for permission, subscribes this browser and tells the server. Returns the resulting state. */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'off';
  const reg = await registration();
  await navigator.serviceWorker.ready;
  const { publicKey } = await get<{ publicKey: string }>('/notifications/push/key');
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }));
  const json = sub.toJSON();
  await post('/notifications/push/subscribe', { endpoint: sub.endpoint, keys: json.keys });
  return 'on';
}

/** Turns push off for this device (the browser's permission stays as the person set it). */
export async function disablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const reg = await navigator.serviceWorker.getRegistration(WORKER);
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api('DELETE', '/notifications/push/subscribe', { endpoint: sub.endpoint }).catch(() => undefined);
    await sub.unsubscribe().catch(() => undefined);
  }
  return Notification.permission === 'denied' ? 'blocked' : 'off';
}
